import { UserMessage } from "./plugins/msgInterface";
import PluginInterface from "./plugins/pluginInterface"
import { v4 as uuidv4 } from 'uuid'

type UUID = `${string}-${string}-${string}-${string}-${string}`

/**
 * Class to handle managing the currently active plugin
 */
export default class PluginSwitchboard {
    PLUGINS: { [index: string]: PluginInterface } = {} // Map of registered plugins indexed by name
    CURRENT_PLUGIN: PluginInterface | undefined // Currently selected plugin

    /**
     * Add a plugin to the list of registered plugins
     * 
     * @param name The name used to reference this plugin
     * @param plugin The plugin object
     * @param default_plugin The name of the default plugin
     */
    register(name: string, plugin: PluginInterface, default_plugin: string) {
        this.PLUGINS[name] = plugin
        if (name === default_plugin) {
            this.CURRENT_PLUGIN = plugin
        }
    }

    /**
     * Provide the current status of the switchboard plugins.
     * 
     * @returns current registered plugins and active plugin
     */
    status() {
        if (!this.CURRENT_PLUGIN) {
            throw Error("No default plugin was specified, your plugin config element should contain the data attribute 'default_plugin'")
        }
        return {
            registered_plugins: Object.keys(this.PLUGINS),
            active_plugin: Object.keys(this.PLUGINS).find((pl) => this.PLUGINS[pl] === this.CURRENT_PLUGIN)
        }
    }

    /**
     * Handles passing control to another plugin
     * 
     * @param original_data The data sent with the original message
     * @param timeout How long to wait for a response
     * @param pluginName The plugin which should assume control
     * @returns either a bot message, undefined or a boolean depending on the response from the plugin
     */
    passControl(original_data: UserMessage, timeout: number, pluginName: string, message: string, payload?: string, options?: object) {
        const plugin = this.PLUGINS[pluginName]
        if (!plugin)
            return {
                message: `Error! An attempt was made to pass control to the ${pluginName} plugin but this plugin does not exist or has not been registered`
            }

        this.CURRENT_PLUGIN = plugin
        const user_message = {
            sender_id: original_data.sender_id,
            payload: payload,
            message: message,
            options: original_data.options
        }

        return this.CURRENT_PLUGIN?.processInput(user_message, timeout)
    }

    /**
     * Process input form a user and dispatch it to the currently active plugin.
     * If the response contains the pass control instruction, switch to the requested 
     * plugin and continue the flow from there.
     * 
     * @param data The message to send to the plugin
     * @param timeout How long to wait for a response
     * @returns either a bot message, undefined or a boolean depending on the response from the plugin
     */
    async processInput(data: UserMessage, timeout: number) {
        const response = await this.CURRENT_PLUGIN?.processInput(data, timeout)
        if (Array.isArray(response)) {

            let pass_control: string = '', payload: string = '', message: string = ''
            // take the latest values as definitive 
            response.forEach(r => {
                if (r.pass_control)
                    pass_control = r.pass_control
                if (r.payload)
                    payload = r.payload
                if (r.message)
                    message = r.message
            })

            if (pass_control)
                return this.passControl(data, timeout, pass_control, message, payload)
        }
        return (response)
    }

    /**
     * Compare the datasets and if there are any remoteDataset values which aren't in the 
     * dataset_defaults, then store these in sessionStorage for easy access by a given plugin.
     * 
     * @param remoteDataset = the dataset fetched remotely via the plugin where available
     * @param dataset_defaults = the default widget dataset
     */
    makeCustomFieldsAvailableToPlugin(remoteDataset: any, dataset_defaults: any) {
        let customFields = {}
        // if the fetched config has keys that aren't in dataset defaults, add them to customFields
        for (const [key, value] of Object.entries(remoteDataset)) {
            if (!dataset_defaults[key]) {
                customFields[key] = value
            }
        }
        // loop new customFields object and save each to sessionStorage for use in given plugin
        for (const [key, value] of Object.entries(customFields)) {
            if (value) {
                sessionStorage.setItem(key.toString(), value.toString())
            }
        }
    }

    /**
     * Fetch a custom config file if available and use this to replace the dataset_defaults.
     * 
     * @param dataset_defaults the default customisations set in the widget
     * @returns a new config json with the relevant styles updated
     */
    async fetchConfig(currentConfig: any, dataset_defaults: any) {
        interface RemoteConfig {
            id?: UUID
            config: boolean | [{ [key: string]: unknown }]
            name?: string
            domain?: string
            active?: boolean
            custom_rasa_server_endpoint?: string
            custom_rasa_server_token?: string
            organisation_name?: string
            sponsor_enabled?: boolean
            further_info_link?: string
            further_info_link_text: string
            further_info_text: string
        }
        let newConfig = currentConfig
        if (currentConfig.remote_config_url) {
            let fetched: RemoteConfig = { config: false}
            const response = await fetch(currentConfig.remote_config_url,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            }
            ).catch((e) => {
                // this error handling could be improved. Currently the widget blank screens when
                // the url has a typo/the server is down etc. error should distinguish this. 
                console.error(e)
            })
            fetched = await response?.json()
            if (fetched?.config) {        
                let newTenantConfig = fetched?.config
                for (const [key, value] of Object.entries(newTenantConfig)) {
                    if (value) {
                        newConfig[key] = newTenantConfig[key]
                    }
                }
                this.makeCustomFieldsAvailableToPlugin(newTenantConfig, dataset_defaults)
            }
            newConfig.tenant_id = fetched?.id
            newConfig.organisation_name = fetched?.organisation_name
            newConfig.sponsor_enabled = fetched?.sponsor_enabled
            newConfig.further_info_link = fetched?.further_info_link
            newConfig.further_info_link_text = fetched?.further_info_link_text
            newConfig.further_info_text = fetched?.further_info_text
        }
        return newConfig
    }

    /**
     * generate a sender ID dependant on current/future plugin.
     * 
     * @param chat_origin origin URL
     * @returns 
     */
    async generateSenderId(chat_origin) {
        let senderId
        if (sessionStorage.getItem('senderId') && sessionStorage.getItem('senderId') !== null && sessionStorage.getItem('senderId') !== undefined) {
            senderId = sessionStorage.getItem('senderId')
        } else {
            if (this.CURRENT_PLUGIN?.data?.coverpage_redirect_plugin) {
                const next_plugin = this.PLUGINS[this.CURRENT_PLUGIN?.data?.coverpage_redirect_plugin]
                if (next_plugin?.getSenderId?.()) {
                    senderId = await next_plugin?.getSenderId()    
                } else {
                    senderId = (chat_origin ? chat_origin + '_' : '') + uuidv4()
                }
            } else {
                if (this.CURRENT_PLUGIN?.getSenderId?.()) {
                    senderId = await this.CURRENT_PLUGIN.getSenderId()    
                } else {
                    senderId = (chat_origin ? chat_origin + '_' : '') + uuidv4()
                }
            }
        }
        return senderId
    }

    /**
     * If a goback method exists within a given plugin, use the go back method.
     * Primarily used to return to a previous page in deterministic flows.
     * 
     * @param send the send method from the widget. Found in Widget.js
     */
    goBack(send: Function) {
        this.CURRENT_PLUGIN?.goBack && this.CURRENT_PLUGIN?.goBack(send)
    }

    /**
     * currentPlugin returns the current plugin active within the widget
     */
    currentPlugin() {
        return this.CURRENT_PLUGIN?.data.plugin
    }
}